import { useState, useCallback, useEffect, useContext } from 'react'
import useOffer from 'hooks/Offers/useOffer'
import CurrencyContext from 'contexts/currencyContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import {
  buildEnrichedMessage,
  buildOfferContextString,
  getOfferPropertyName,
} from '../utils/messageEnrichment'

interface UseMessageContextArgs {
  streamingSubmit: (message: string) => Promise<void>
  setFollowTail: (follow: boolean) => void
  setMessageInputValue: (value: string) => void
  focusChatInput?: () => void
  prefilledMultiChoiceSelection?: Array<string>
}

export function useMessageContext({
  streamingSubmit,
  setFollowTail,
  setMessageInputValue,
  focusChatInput,
  prefilledMultiChoiceSelection = [],
}: UseMessageContextArgs) {
  const currentCurrency = useContext(CurrencyContext)

  const [activeMultiChoiceSelection, setActiveMultiChoiceSelection] = useState<Array<string>>([])
  const [mobileSelectionText, setMobileSelectionText] = useState<string | undefined>(undefined)
  const [offerContextTag, setOfferContextTag] = useState<{ label: string; offerId: string } | undefined>(undefined)

  // Fetch offer data for context tag
  const [contextOfferData] = useOffer<App.AnyOffer>(offerContextTag?.offerId || '')

  // Update context tag label when offer data loads
  useEffect(() => {
    if (contextOfferData && offerContextTag) {
      const name = getOfferPropertyName(contextOfferData)
      if (name && offerContextTag.label !== name) {
        setOfferContextTag({ label: name, offerId: offerContextTag.offerId })
      }
    }
  }, [contextOfferData, offerContextTag])

  const handleSubmit = useCallback(async(message: string, options?: SendMessageOptions) => {
    setMessageInputValue('')
    setFollowTail(false)

    const offerContext = offerContextTag && contextOfferData
      ? buildOfferContextString(contextOfferData, currentCurrency)
      : undefined
    const multiChoiceSelection = [...new Set([...prefilledMultiChoiceSelection, ...activeMultiChoiceSelection])]

    const enrichedMessage = buildEnrichedMessage({
      message,
      multiChoiceSelection,
      sendMessageOptions: options,
      mobileSelectionText,
      offerContext,
    })

    // Clear state after building message
    if (offerContextTag) setOfferContextTag(undefined)
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)

    return streamingSubmit(enrichedMessage)
  }, [
    streamingSubmit,
    setFollowTail,
    setMessageInputValue,
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    contextOfferData,
    currentCurrency,
    prefilledMultiChoiceSelection,
  ])

  const handleMultiChoiceSelectionChange = useCallback((selection: Array<string>) => {
    setActiveMultiChoiceSelection(selection)
    focusChatInput?.()
  }, [focusChatInput])

  const handleRemoveTag = useCallback((tag: string) => {
    setActiveMultiChoiceSelection(prev => prev.filter(s => s !== tag))
    if (mobileSelectionText && tag === mobileSelectionText) {
      setMobileSelectionText(undefined)
    }
    if (tag === offerContextTag?.label) {
      setOfferContextTag(undefined)
    }
  }, [mobileSelectionText, offerContextTag])

  const submitDirect = useCallback(async(message: string) => {
    setMessageInputValue('')
    setFollowTail(false)
    if (offerContextTag) setOfferContextTag(undefined)
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)
    return streamingSubmit(message)
  }, [streamingSubmit, setFollowTail, setMessageInputValue, offerContextTag])

  const resetSelectionState = useCallback(() => {
    setActiveMultiChoiceSelection([])
    setMobileSelectionText(undefined)
    setOfferContextTag(undefined)
  }, [])

  return {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  }
}
